import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Reveal from "@/components/base/Reveal";
import BackToAll from "@/components/feature/BackToAll";
import { buildFaqPageSchema } from "@/components/feature/FaqAccordion";
import EnquirySection from "@/components/feature/EnquirySection";
import EnquiryModal from "@/components/feature/EnquiryModal";
import StickyEnquiryBar from "@/components/feature/StickyEnquiryBar";
import { buildBreadcrumbSchema, type BreadcrumbItem } from "@/components/feature/Breadcrumbs";
import Seo, { getAbsoluteUrl } from "@/components/feature/Seo";
import { brand } from "@/mocks/siteData";
import { usePublicFarmers } from "@/hooks/usePublicCatalog";
import { buildFarmerFaqs } from "./farmerFaqs";
import FarmerDetailHero from "./components/FarmerDetailHero";
import FarmerDetailStory from "./components/FarmerDetailStory";
import FarmerDetailGallery from "./components/FarmerDetailGallery";
import FarmerDetailExperience from "./components/FarmerDetailExperience";

export default function FarmerDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { data: farmerProfiles, loading, error, reload } = usePublicFarmers();
  const [enquiryOpen, setEnquiryOpen] = useState(false);

  const farmer = slug ? farmerProfiles.find((item) => item.slug === slug) : undefined;

  if (loading && !farmer) {
    return (
      <div className="min-h-screen bg-background-50 overflow-x-hidden">
        <Navbar />
        <main className="w-full px-4 md:px-8 pt-32 pb-24 min-h-[70vh] flex items-center justify-center">
          <div className="flex flex-col items-center gap-4 text-foreground-600">
            <span className="w-12 h-12 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin"></span>
            <p className="text-sm">Loading farmer…</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!farmer) {
    return (
      <div className="min-h-screen bg-background-50 overflow-x-hidden">
        {/* Unknown slug: noindex, so a dead farmer URL is never indexed as a copy of the previous page. */}
        <Seo
          title={error ? "Unable to load this profile" : `Farmer not found | ${brand.name}`}
          description="We couldn't find that farmer profile. Meet the real farm families who host Rythugaru experiences across Konaseema, Andhra Pradesh."
          path={`/farmers/${slug ?? ""}`}
          noindex
        />
        <Navbar />
        <main className="w-full px-4 md:px-8 pt-32 pb-24 min-h-[70vh] flex items-center justify-center">
          <Reveal className="text-center max-w-lg">
            <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
              <i className="ri-user-voice-line text-3xl"></i>
            </span>
            <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 font-semibold mt-6">
              {error ? "We couldn't load this profile" : "Farmer not found"}
            </h1>
            <p className="mt-4 text-foreground-600 leading-relaxed">
              {error
                ? error
                : "We couldn't find that farmer profile. Meet the families who host Rythugaru experiences across the delta instead."}
            </p>
            {error ? (
              <button
                type="button"
                onClick={reload}
                className="mt-8 inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm font-semibold px-7 py-4 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-refresh-line"></i>
                Try again
              </button>
            ) : (
              <Link
                to="/farmers"
                className="mt-8 inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm font-semibold px-7 py-4 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Meet our farmers
                <i className="ri-arrow-right-line"></i>
              </Link>
            )}
          </Reveal>
        </main>
        <Footer />
      </div>
    );
  }

  const faqs = buildFarmerFaqs(farmer);
  const breadcrumbs: BreadcrumbItem[] = [
    { label: "Home", to: "/" },
    { label: "Meet Our Farmers", to: "/farmers" },
    { label: farmer.name },
  ];

  return (
    <div className="min-h-screen bg-background-50 overflow-x-hidden">
      <Seo
        title={`${farmer.name} | ${farmer.crop} Farmer, ${farmer.village}`}
        description={farmer.intro}
        tokens={{ title: farmer.name, description: farmer.intro }}
        path={`/farmers/${farmer.slug}`}
        keywords={`${farmer.name}, farmer ${farmer.village}, ${farmer.crop} farmer, meet the farmer, agri tourism Konaseema`}
        image={farmer.portrait}
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "Person",
            name: farmer.name,
            description: farmer.intro,
            image: farmer.portrait,
            url: getAbsoluteUrl(`/farmers/${farmer.slug}`),
            jobTitle: `Farmer — ${farmer.crop}`,
            knowsAbout: farmer.specialties,
            address: {
              "@type": "PostalAddress",
              addressLocality: farmer.village,
              addressRegion: "Andhra Pradesh",
              addressCountry: "IN",
            },
            worksFor: {
              "@type": "Organization",
              name: brand.fullName,
            },
          },
          buildFaqPageSchema(faqs),
          buildBreadcrumbSchema(breadcrumbs, getAbsoluteUrl(`/farmers/${farmer.slug}`)),
        ]}
      />
      <Navbar />
      <main>
        <FarmerDetailHero farmer={farmer} breadcrumbs={breadcrumbs} />
        <FarmerDetailStory farmer={farmer} />
        <FarmerDetailGallery farmer={farmer} />
        <FarmerDetailExperience farmer={farmer} />
        <EnquirySection
          eyebrow="Plan Your Visit"
          heading={`Plan a Visit With ${farmer.name}`}
          intro={`Common questions from visitors planning to meet ${farmer.name} — and one place to start the conversation.`}
          faqs={faqs}
          panelHeading="Enquire About This Farmer"
          panelIntro={`Share a few details about your group and the season you are planning. Our team replies within 24 hours with availability and a clear plan for meeting ${farmer.name}.`}
          onEnquire={() => setEnquiryOpen(true)}
        />
        <BackToAll to="/farmers" label="Back to all farmers" icon="ri-user-voice-line" />
        <StickyEnquiryBar
          title={farmer.name}
          eyebrow={`Farmer in ${farmer.village}`}
          onEnquire={() => setEnquiryOpen(true)}
        />
        <EnquiryModal
          open={enquiryOpen}
          onClose={() => setEnquiryOpen(false)}
          kind="farmer"
          options={farmerProfiles.map((item) => ({ value: item.name, label: item.name }))}
          defaultValue={farmer.name}
        />
      </main>
      <Footer />
    </div>
  );
}